'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { usePathname as useNextPathname } from 'next/navigation'
import { useIsI18nContext } from '@/hooks/useIsI18nContext'
import { useSafeTranslations } from '@/hooks/useSafeTranslations'
import { closeAllSidebars, openSidebar } from './headerUtils'
import { routing } from '@/i18n/routing'

/**
 * Custom hook that encapsulates all Header state and logic
 * Extracted from Header.tsx to separate concerns
 */
export function useHeaderState() {
  // Estados principales (líneas 82-86 de Header.tsx)
  const [headerLoaded, setHeaderLoaded] = useState(false)
  const [navbarVisible, setNavbarVisible] = useState(true)
  const [navbarDark, setNavbarDark] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null)

  // Refs (líneas 89-94)
  const headerRef = useRef<HTMLElement>(null)
  const lastScrollRef = useRef(0)
  const openSidebarsRef = useRef<Set<string>>(new Set())
  const sidebarCloseTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Contexto i18n y pathname
  const isI18n = useIsI18nContext()
  const nextPathname = useNextPathname()
  const pathname = nextPathname || '/'

  // Traducciones (líneas 73-76)
  const t = useSafeTranslations('common')
  const tHeader = useSafeTranslations('header')
  const tFooter = useSafeTranslations('footer')
  const tLodgesRegions = useSafeTranslations('lodges.regions')

  // Homepage check
  const isHomepage = pathname === '/' || pathname === '/en/' || pathname === '/es/' || pathname === '/pt/'

  // ============================================
  // INICIALIZAR EVENT LISTENERS (EXACTO COMO ASTRO)
  // Líneas 200-376 de Header.tsx
  // ============================================

  useEffect(() => {
    // Wrapper para closeAllSidebars con ref
    const closeAllSidebarsWrapper = (except: string[] = []) => {
      closeAllSidebars(except, openSidebarsRef)
    }

    // Wrapper para openSidebar con refs
    const openSidebarWrapper = (sidebarName: string, triggerButton: Element | null = null) => {
      openSidebar(sidebarName, triggerButton, openSidebarsRef, closeAllSidebarsWrapper)
    }

    // Handler para botón hamburguesa (EXACTO COMO ASTRO)
    const handleMenuClick = (e: Event) => {
      e.preventDefault()
      const trigger = e.currentTarget as Element
      const sidebarName = trigger.getAttribute('data-sidebar-trigger')

      if (!sidebarName) return

      // En móvil (<1024px), abrir mobile menu
      if (window.innerWidth < 1024) {
        const mobileMenuElement = document.querySelector('[data-mobile-menu]')

        if (mobileMenuElement?.classList.contains('active')) {
          // Cerrar mobile menu — restaurar scroll
          const scrollY = document.body.style.top
          document.body.style.position = ''
          document.body.style.top = ''
          document.body.style.width = ''
          document.body.style.overflow = ''
          document.documentElement.style.overflow = ''
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
          setMobileMenuOpen(false)
        } else {
          // Abrir mobile menu — bloquear scroll (iOS-proof)
          const scrollY = window.scrollY
          document.body.style.position = 'fixed'
          document.body.style.top = `-${scrollY}px`
          document.body.style.width = '100%'
          document.body.style.overflow = 'hidden'
          document.documentElement.style.overflow = 'hidden'
          setMobileMenuOpen(true)
        }
        return
      }

      // Desktop (>=1024px): comportamiento original
      // Si CUALQUIER sidebar está abierto, cerrar TODOS sin abrir nada
      if (openSidebarsRef.current.size > 0) {
        closeAllSidebarsWrapper()
        return
      }

      // Si ningún sidebar está abierto, abrir menú principal
      openSidebarWrapper(sidebarName, trigger)
    }

    // Handler para botones navbar desktop (mouseenter - hover)
    const handleNavLinkHover = (e: Event) => {
      // ⚠️ CRÍTICO: NO hacer nada si el menú principal está abierto
      if (openSidebarsRef.current.has('menu-principal')) {
        return
      }

      const trigger = e.currentTarget as Element
      const sidebarName = trigger.getAttribute('data-sidebar-trigger')

      if (!sidebarName) return

      // Cancelar cualquier cierre pendiente
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current)
        sidebarCloseTimerRef.current = null
      }

      // Abrir sidebar al pasar mouse
      openSidebarWrapper(sidebarName, trigger)
    }

    // Handler para cuando mouse sale del área navbar/sidebar
    const handleMouseLeave = () => {
      // Esperar 500ms antes de cerrar
      sidebarCloseTimerRef.current = setTimeout(() => {
        // Solo cerrar sidebars de navbar (lodges, adventures), NO menu-principal
        closeAllSidebarsWrapper(['menu-principal'])
      }, 500)
    }

    // Handler para cuando mouse entra al sidebar (cancelar cierre)
    const handleSidebarMouseEnter = () => {
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current)
        sidebarCloseTimerRef.current = null
      }
    }

    // Handler para click en overlay
    const handleOverlayClick = () => {
      closeAllSidebarsWrapper()
    }

    // Handler para botones dentro de sidebars
    const handleSidebarButtonClick = (e: Event) => {
      const trigger = e.currentTarget as Element
      const sidebarName = trigger.getAttribute('data-sidebar-trigger')

      if (!sidebarName) return

      // Cancelar cualquier cierre pendiente
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current)
        sidebarCloseTimerRef.current = null
      }

      // Abrir el sidebar correspondiente
      openSidebarWrapper(sidebarName, trigger)
    }

    // Handler para escape key
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openSidebarsRef.current.size > 0) {
        closeAllSidebarsWrapper()
      }
    }

    // INICIALIZAR LISTENERS (EXACTO COMO ASTRO)
    const menuToggle = document.querySelector('.menu-toggle')
    const navLinks = document.querySelectorAll('.nav-link[data-sidebar-trigger]')
    const navLeftContainer = document.querySelector('.nav-left-container')
    const sidebarButtons = document.querySelectorAll('.sidebar [data-sidebar-trigger]')
    const sidebars = document.querySelectorAll('.sidebar')
    const sidebarOverlay = document.querySelector('.sidebar-overlay')

    // Botón hamburguesa MENU: mantener click
    if (menuToggle) {
      menuToggle.addEventListener('click', handleMenuClick)
    }

    // Botones navbar desktop: hover
    navLinks.forEach(link => {
      link.addEventListener('mouseenter', handleNavLinkHover)
    })

    // Botones dentro de sidebars: solo mouseenter
    sidebarButtons.forEach(button => {
      button.addEventListener('mouseenter', handleSidebarButtonClick)
    })

    // Mouse leave desde nav container
    if (navLeftContainer) {
      navLeftContainer.addEventListener('mouseleave', handleMouseLeave)
    }

    // Sidebars: prevenir cierre cuando mouse está sobre ellos
    sidebars.forEach(sidebar => {
      sidebar.addEventListener('mouseenter', handleSidebarMouseEnter)
      sidebar.addEventListener('mouseleave', handleMouseLeave)
    })

    // Overlay click
    if (sidebarOverlay) {
      sidebarOverlay.addEventListener('click', handleOverlayClick)
    }

    // Escape key
    document.addEventListener('keydown', handleEscapeKey)

    // Cleanup
    return () => {
      // Clear pending sidebar close timer to prevent memory leak
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current)
        sidebarCloseTimerRef.current = null
      }

      if (menuToggle) {
        menuToggle.removeEventListener('click', handleMenuClick)
      }
      navLinks.forEach(link => {
        link.removeEventListener('mouseenter', handleNavLinkHover)
      })
      sidebarButtons.forEach(button => {
        button.removeEventListener('mouseenter', handleSidebarButtonClick)
      })
      if (navLeftContainer) {
        navLeftContainer.removeEventListener('mouseleave', handleMouseLeave)
      }
      sidebars.forEach(sidebar => {
        sidebar.removeEventListener('mouseenter', handleSidebarMouseEnter)
        sidebar.removeEventListener('mouseleave', handleMouseLeave)
      })
      if (sidebarOverlay) {
        sidebarOverlay.removeEventListener('click', handleOverlayClick)
      }
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, []) // Solo se ejecuta una vez al montar

  // ============================================
  // HOMEPAGE ANIMATION (Líneas 382-391)
  // ============================================

  useEffect(() => {
    if (isHomepage) {
      const timer = setTimeout(() => {
        setHeaderLoaded(true)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setHeaderLoaded(true)
    }
  }, [isHomepage])

  // ============================================
  // SCROLL DETECTION (Líneas 397-422)
  // ============================================

  useEffect(() => {
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScroll = window.pageYOffset
          const scrollThreshold = 100

          if (currentScroll > lastScrollRef.current && currentScroll > scrollThreshold) {
            setNavbarVisible(false)
          } else if (currentScroll < lastScrollRef.current) {
            setNavbarVisible(true)
          }

          lastScrollRef.current = currentScroll
          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // ============================================
  // NAVBAR ADAPTATIVO - DETECCIÓN DE LUMINOSIDAD (Líneas 428-582)
  // ============================================

  useEffect(() => {
    // Helper functions
    const calculateRelativeLuminance = (r: number, g: number, b: number): number => {
      const [rs, gs, bs] = [r / 255, g / 255, b / 255].map(val => {
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
      })
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
    }

    const detectBackgroundLuminosity = () => {
      const headerEl = headerRef.current
      if (!headerEl) return null

      // Obtener color visual JUSTO debajo del navbar
      const headerRect = headerEl.getBoundingClientRect()
      const sampleX = headerRect.left + headerRect.width / 2
      const sampleY = headerRect.bottom + 5 // Muy cerca del navbar

      headerEl.style.pointerEvents = 'none'
      const elementBehind = document.elementFromPoint(sampleX, sampleY)
      headerEl.style.pointerEvents = ''

      if (!elementBehind) return null

      // DETECCIÓN DE HERO: Si estamos sobre un hero (data-hero-background), navbar siempre transparente
      // Buscar si el elemento o alguno de sus ancestros tiene data-hero-background
      const isOverHero = elementBehind.closest('[data-hero-background]') !== null

      if (isOverHero) {
        return { luminance: 0, isLightBackground: false }
      }

      // Recolectar TODOS los backgrounds en la jerarquía (compositing)
      const backgrounds: Array<{ r: number; g: number; b: number; a: number }> = []
      let currentElement: Element | null = elementBehind

      while (currentElement && currentElement !== document.body) {
        // Skip mobile menu elements
        if (currentElement.classList?.contains('mobile-menu') ||
            currentElement.classList?.contains('mobile-menu-content') ||
            currentElement.classList?.contains('mobile-menu-backdrop')) {
          currentElement = currentElement.parentElement
          continue
        }

        const style = getComputedStyle(currentElement)
        const bgColor = style.backgroundColor

        // Parse color (regex sin doble escape)
        const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
        if (match) {
          const r = parseInt(match[1])
          const g = parseInt(match[2])
          const b = parseInt(match[3])
          const a = match[4] ? parseFloat(match[4]) : 1

          // Solo agregar si tiene opacidad
          if (a > 0) {
            backgrounds.push({ r, g, b, a })
          }

          // Si encontramos un fondo opaco, detener
          if (a === 1) break
        }

        currentElement = currentElement.parentElement
      }

      // Si no hay backgrounds, usar body
      if (backgrounds.length === 0) {
        const bodyBg = getComputedStyle(document.body).backgroundColor
        const match = bodyBg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
        if (match) {
          backgrounds.push({
            r: parseInt(match[1]),
            g: parseInt(match[2]),
            b: parseInt(match[3]),
            a: match[4] ? parseFloat(match[4]) : 1
          })
        }
      }

      if (backgrounds.length === 0) return null

      // Composite backgrounds (alpha blending de atrás hacia adelante)
      let finalR = 255
      let finalG = 255
      let finalB = 255

      // Procesar de atrás hacia adelante
      for (let i = backgrounds.length - 1; i >= 0; i--) {
        const bg = backgrounds[i]
        const alpha = bg.a

        finalR = Math.round(bg.r * alpha + finalR * (1 - alpha))
        finalG = Math.round(bg.g * alpha + finalG * (1 - alpha))
        finalB = Math.round(bg.b * alpha + finalB * (1 - alpha))
      }

      const luminance = calculateRelativeLuminance(finalR, finalG, finalB)
      // Threshold 0.6 para fondos claros (blancos, grises claros como #f8f9fa, cremas, etc.)
      // Luminance de blanco puro = 1.0, #f8f9fa ≈ 0.95, #ddd ≈ 0.71
      const isLightBackground = luminance > 0.6

      return { luminance, isLightBackground }
    }

    const applyNavbarTheme = () => {
      // No aplicar si mobile menu está abierto
      if (mobileMenuOpen) return

      const result = detectBackgroundLuminosity()
      if (!result) return

      setNavbarDark(result.isLightBackground)
    }

    // Scroll handler con RAF
    let rafId: number | null = null
    let isChecking = false

    const handleScrollTheme = () => {
      if (isChecking) return
      isChecking = true

      if (rafId) cancelAnimationFrame(rafId)

      rafId = requestAnimationFrame(() => {
        applyNavbarTheme()
        isChecking = false
      })
    }

    window.addEventListener('scroll', handleScrollTheme, { passive: true })

    // Detección inicial
    const initialDelay = isHomepage ? 5400 : 100
    const timer = setTimeout(applyNavbarTheme, initialDelay)

    return () => {
      window.removeEventListener('scroll', handleScrollTheme)
      clearTimeout(timer)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isHomepage, mobileMenuOpen])

  // ============================================
  // MOBILE FUNCTIONS (Líneas 588-596)
  // ============================================

  const handleMobileMenuLinkClick = useCallback((e: React.MouseEvent, url: string) => {
    e.preventDefault()
    setMobileMenuOpen(false)
    setMobileSubmenuOpen(null)
    // Restaurar scroll (iOS-proof)
    const scrollY = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    window.scrollTo(0, parseInt(scrollY || '0') * -1)

    // Prepend locale prefix for i18n routes (e.g., /experiences/x → /en/experiences/x)
    // Validate against supported locales to avoid misrouting from /booking/ pages
    // Non-localized routes (outside app/[locale]/) must be excluded from prefixing
    const NON_LOCALIZED_PREFIXES = ['/booking', '/checkout', '/confirmation', '/admin', '/login']
    const segment = pathname.split('/')[1]
    const locale = isI18n && (routing.locales as readonly string[]).includes(segment) ? segment : ''
    const isNonLocalized = NON_LOCALIZED_PREFIXES.some(p => url === p || url.startsWith(`${p}/`))
    const localizedUrl = locale && !isNonLocalized && !url.startsWith(`/${locale}/`) && !url.startsWith(`/${locale}`)
      ? `/${locale}${url}`
      : url

    setTimeout(() => {
      window.location.href = localizedUrl
    }, 850)
  }, [isI18n, pathname])

  // ============================================
  // RETURN ALL STATE AND ACTIONS
  // ============================================

  return {
    // Estados
    headerLoaded,
    navbarVisible,
    navbarDark,
    mobileMenuOpen,
    mobileSubmenuOpen,
    setMobileMenuOpen,
    setMobileSubmenuOpen,
    // Refs
    headerRef,
    openSidebarsRef,
    // Context
    isI18n,
    pathname,
    isHomepage,
    // Traducciones
    t,
    tHeader,
    tFooter,
    tLodgesRegions,
    // Handlers
    handleMobileMenuLinkClick,
  }
}
